import { AvailabilityStatusPresentation, AvailabilityStatusType } from "@dustin-web/microsite-data/src/graphql/product/generated/graphql-types";
import type { GenericAvailabilityType } from "../../cards";
import { type AvailabilityType, AvailabilityTypes } from "./types";

/**
 * Resolves the AvailabilityType for a given product availability object.
 *
 * @param {GenericAvailabilityType} availability - The availability entity, or undefined/null if not available.
 * @returns {AvailabilityType} - The resolved availability type based on key/status logic.
 *
 * Logic summary:
 * - Returns NOT_MIGRATED_TYPE if availability is missing.
 * - Handles direct mappings via a key-type map for clarity and maintainability.
 * - Applies business rules for in-stock logic, downloadable items, and special availability cases.
 * - Returns a fallback type if no specific rule matches.
 */
export function getAvailabilityType(
  availability: GenericAvailabilityType
): AvailabilityType {
  if (!availability) {
    return AvailabilityTypes.NOT_MIGRATED_TYPE;
  }

  const { availabilityStatusPresentation, availabilityDetails, internalStock } = availability;
  const { key } = availabilityDetails ?? {};

  const isDownloadable = key === AvailabilityStatusType.ProductAvailabilityEsd;

  const AVAILIABILITY_TYPE_MAP: Partial<Record<AvailabilityStatusType, AvailabilityType>> = {
    [AvailabilityStatusType.ProductDistributorLeadTimeOneTwoDays]: AvailabilityTypes.SUPPLIER_STOCK_SHIPPING_WITHIN_48H,
    [AvailabilityStatusType.ProductEtaLeadTimeEnumOne2TwoWeeks]: AvailabilityTypes.WEEKS_FROM_ORDER_DATE,
    [AvailabilityStatusType.ProductAvailabilityShippingDateAtTheEarliest]: AvailabilityTypes.NON_STOCK_ITEM_EARLIEST_DATE,
    [AvailabilityStatusType.ProductAvailabilityShippingDate]: AvailabilityTypes.NON_STOCK_ITEM_EARLIEST_DATE,
    [AvailabilityStatusType.ProductAvailabilityOutOfStockComingSoon]: AvailabilityTypes.PRODUCT_AVAILABILITY_OUT_OF_STOCK_COMING_SOON,
    [AvailabilityStatusType.ProductAvailabilityEsd]: AvailabilityTypes.IMMEDIATE_DOWNLOAD,
    [AvailabilityStatusType.ProductAvailabilityEstimatedShippingDate]: AvailabilityTypes.ESTIMATED_IN_STORE_BY_DATE,
    [AvailabilityStatusType.ProductEstimatedInOurStore]: AvailabilityTypes.ESTIMATED_IN_STORE_BY_DATE,
    [AvailabilityStatusType.ProductDistributorLeadTimeThreeFourDays]: AvailabilityTypes.SUPPLIER_STOCK_SHIPPING_WITHIN_THREE_FOUR_DAYS,
    [AvailabilityStatusType.ProductDistributorLeadTimeFourSixDays]: AvailabilityTypes.SUPPLIER_STOCK_SHIPPING_WITHIN_FOUR_SIX_DAYS,
    [AvailabilityStatusType.ProductDistributorLeadTimeSixEightDays]: AvailabilityTypes.SUPPLIER_STOCK_SHIPPING_WITHIN_SIX_EIGHT_DAYS,
    [AvailabilityStatusType.ProductDistributorLeadTimeTenTwelveDays]: AvailabilityTypes.SUPPLIER_STOCK_SHIPPING_WITHIN_TEN_TWELVE_DAYS,
    [AvailabilityStatusType.ProductAvailabilityShortage]: AvailabilityTypes.STOCK_SHORTAGE_ITEM
  } as const;

  if (internalStock?.showAlwaysInStock && !isDownloadable) {
    return AvailabilityTypes.SHOW_ALWAYS_IN_STOCK;
  }

  if (availabilityStatusPresentation === AvailabilityStatusPresentation.ShowAsImmediateShipping && !isDownloadable) {
    return AvailabilityTypes.IN_STOCK_IMMEDIATE_SHIPPING;
  }

  if (availabilityStatusPresentation === AvailabilityStatusPresentation.ShowAsOutOfStock) {
    return AvailabilityTypes.NON_STOCK_ITEM;
  }

  return (key && AVAILIABILITY_TYPE_MAP[key]) ?? AvailabilityTypes.NOT_MIGRATED_TYPE
}
